import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { CsvButton } from "../components/ReportButtons";
import {
  Building2, FileText, Users, Map, ListChecks, Lock, Compass, Wrench,
} from "lucide-react";

function Tile({ to, icon: Icon, title, body, testId }) {
  return (
    <Link to={to} className="cesco-card p-5 hover:bg-gray-50 block transition-colors"
          data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--theme-accent-50)] flex items-center justify-center shrink-0">
          <Icon size={18}/>
        </div>
        <div>
          <div className="font-semibold">{title}</div>
          <div className="text-xs text-gray-500 mt-0.5 leading-snug">{body}</div>
        </div>
      </div>
    </Link>
  );
}

function AdminDumpRow({ path, title, body, testId }) {
  return (
    <div className="cesco-card p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
      <div>
        <div className="font-semibold flex items-center gap-2">
          {title} <span className="pill pill-grey text-xs">Admin</span>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">{body}</div>
      </div>
      <CsvButton path={path} label="Download CSV" testId={testId}/>
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="reports-hub">
      <div>
        <h1 className="text-2xl font-bold">Reports</h1>
        <p className="text-sm text-gray-500 mt-1">
          On-screen reports support filters, CSV export, and printing. Admin-only
          dumps export the full database for analysis in Excel.
        </p>
      </div>

      {/* On-screen reports */}
      <div>
        <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3">
          On-screen reports
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Tile to="/companies" icon={Building2} title="Companies"
                body="Filter by state, tier, segment, country, current customer, etc. Excel + Print."
                testId="tile-companies"/>
          <Tile to="/reports/sales-activity" icon={ListChecks} title="Sales Activity"
                body="Tasks, notes, and discovery entries by rep + date range."
                testId="tile-sales-activity"/>
          <Tile to="/quotes" icon={FileText} title="Quote Register"
                body="All quotes with rep column, filters, Excel + Print."
                testId="tile-quotes"/>
          <Tile to="/reports/plan-sales-trip" icon={Map} title="Plan Sales Trip"
                body="Pick a region + segment, get the contact list to plan your week."
                testId="tile-plan-sales-trip"/>
          <Tile to="/companies" icon={Compass} title="Discovery (per company)"
                body="Open any company → Discovery tab. Densified table view."
                testId="tile-discovery"/>
        </div>
      </div>

      {/* Admin dumps */}
      {isAdmin && (
        <div>
          <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Lock size={13}/> Admin Excel dumps
          </h2>
          <div className="space-y-2">
            <AdminDumpRow path="/reports/admin/company-full-dump.csv"
                          title="Company full dump"
                          body="One row per (company, contact) pair - all fields. For data-quality review."
                          testId="dump-company-full"/>
            <AdminDumpRow path="/reports/admin/contact-full-dump.csv"
                          title="Contact full dump"
                          body="One row per contact with all contact fields and their company's key fields."
                          testId="dump-contact-full"/>
            <AdminDumpRow path="/reports/admin/discovery-mixer.csv"
                          title="Discovery - Mixer (wide)"
                          body="One row per Mixer-segment company; each discovery question is a column."
                          testId="dump-discovery-mixer"/>
            <AdminDumpRow path="/reports/admin/discovery-drill.csv"
                          title="Discovery - Drill (wide)"
                          body="One row per non-Mixer-segment company; each discovery question is a column."
                          testId="dump-discovery-drill"/>
            <AdminDumpRow path="/reports/admin/equipment-full-dump.csv"
                          title="Equipment full dump"
                          body="One row per equipment record with manufacturer, model, serial, DOM."
                          testId="dump-equipment-full"/>
          </div>
        </div>
      )}
    </div>
  );
}
